import { useState, useEffect } from 'react';
import { SkewedAnalyticsService } from '../../../../../services/api/skewed-analytics.service';

interface UploadReportParams {
    networkId: number;
    file: File;
}

interface DeleteReportParams {
    networkId: number;
    reportId: string;
}

interface HookCallbacks {
    onSuccess?: (data?: unknown) => void;
    onError?: (error: Error) => void;
}

interface Report {
    reportId: string;
    fileName: string;
    originalName: string;
    size: number;
    uploadedAt: number;
    uploadedBy: string;
    expiresAt: number;
    downloadCount: number;
    deletedAt?: number | null;
}

/**
 * Hook to upload a report file
 */
export const useUploadReport = ({ onSuccess, onError }: HookCallbacks = {}) => {
    const [isLoading, setIsLoading] = useState(false);

    const mutate = async ({ networkId, file }: UploadReportParams) => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            // Call skewed-analytics service directly
            const data = await SkewedAnalyticsService.uploadReport(networkId, formData);

            if (onSuccess) {
                onSuccess(data);
            }
        } catch (error) {
            if (onError) {
                const errorMessage = (error as { message?: string })?.message || 'Upload failed';
                onError(new Error(errorMessage));
            }
        } finally {
            setIsLoading(false);
        }
    };

    return { mutate, isLoading };
};

/**
 * Hook to fetch reports for a network
 */
export const useGetReports = (networkId: number) => {
    const [data, setData] = useState<Report[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchReports = async () => {
        if (!networkId) {
            setData([]);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            // Call skewed-analytics service directly (admin endpoint)
            const response = await SkewedAnalyticsService.listReports(networkId);
            if (Array.isArray(response)) {
                setData(response);
            } else if (response && typeof response === 'object' && 'reports' in response) {
                setData((response as { reports: Report[] }).reports || []);
            } else {
                setData([]);
            }
        } catch (err) {
            const errorMessage = (err as { message?: string })?.message || 'Failed to fetch reports';
            setError(new Error(errorMessage));
            setData([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, [networkId]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchReports,
    };
};

/**
 * Hook to delete a report
 */
export const useDeleteReport = ({ onSuccess, onError }: HookCallbacks = {}) => {
    const [isLoading, setIsLoading] = useState(false);

    const mutate = async ({ networkId, reportId }: DeleteReportParams) => {
        setIsLoading(true);
        try {
            // Call skewed-analytics service directly (admin hard delete)
            await SkewedAnalyticsService.deleteReport(networkId, reportId);
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            if (onError) {
                const errorMessage = (error as { message?: string })?.message || 'Delete failed';
                onError(new Error(errorMessage));
            }
        } finally {
            setIsLoading(false);
        }
    };

    return { mutate, isLoading };
};

interface DownloadReportParams {
    reportId: string;
    fileName: string;
    networkId: number;
}

/**
 * Hook to download a report
 */
export const useDownloadReport = ({ onSuccess, onError }: HookCallbacks = {}) => {
    const [isLoading, setIsLoading] = useState(false);

    const mutate = async ({ reportId, fileName, networkId }: DownloadReportParams) => {
        setIsLoading(true);
        try {
            // Get signed download URL from skewed-analytics
            const response = (await SkewedAnalyticsService.getDownloadUrl(networkId, reportId)) as { signedUrl: string };
            const { signedUrl } = response;

            // Open the signed URL in a new tab to download the file
            const link = document.createElement('a');
            link.href = signedUrl;
            link.download = fileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            if (onError) {
                const errorMessage = (error as { message?: string })?.message || 'Download failed';
                onError(new Error(errorMessage));
            }
        } finally {
            setIsLoading(false);
        }
    };

    return { mutate, isLoading };
};

interface RevertReportParams {
    networkId: number;
    reportId: string;
}

/**
 * Hook to revert a soft-deleted report
 */
export const useRevertReportDeletion = ({ onSuccess, onError }: HookCallbacks = {}) => {
    const [isLoading, setIsLoading] = useState(false);

    const mutate = async ({ networkId, reportId }: RevertReportParams) => {
        setIsLoading(true);
        try {
            // Call skewed-analytics service directly (admin revert)
            await SkewedAnalyticsService.revertReportDeletion(networkId, reportId);
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            if (onError) {
                const errorMessage = (error as { message?: string })?.message || 'Revert failed';
                onError(new Error(errorMessage));
            }
        } finally {
            setIsLoading(false);
        }
    };

    return { mutate, isLoading };
};
